import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { Info, Plus, ScrollText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/components/ui/sonner';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { invalidateQueries } from '@/lib/query-client';
import { Link } from 'react-router';
import { useIntegrations, useUrlSearch } from '@/hooks';
import {
	useDeleteSocialRule,
	useSetSocialRuleStatus,
	useSocialRules,
} from '@/hooks/use-social-agent';
import { findMetaChannels } from '@/modules/integrations/meta-contract';
import { RulesTable } from '@/modules/social-agent/components/rules-table';
import { RuleFormDialog } from '@/modules/social-agent/components/rule-form-dialog';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { SocialAgentEmptyState } from '@/modules/social-agent/components/social-agent-empty-state';
import { CREATABLE_ACTION_TYPES } from '@/modules/social-agent/rule-form-config';
import type { ChannelHandleMap } from '@/modules/social-agent/components/channel-chips';
import {
	SOCIAL_ACTION_LABELS,
	SOCIAL_RULE_FILTERS,
	EMPTY_CHANNEL_FILTER,
	matchesChannelFilter,
	delayButtonLabel,
	matchesSearch,
	type ChannelFilterValue,
	type SocialRuleFilter,
} from '@/modules/social-agent/constants';
import { ChannelFilter } from '@/modules/social-agent/components/channel-filter';
import { PILL_BUTTON } from '@/modules/social-agent/ui-chrome';
import { DelayReplyDialog } from '@/modules/social-agent/components/delay-reply-dialog';
import { socialAgentService } from '@/services/social-agent.service';
import type {
	ReplyDelay,
	SocialActionType,
	SocialRule,
	SocialRuleStatus,
} from '@/modules/social-agent/types';

const RulesTableSkeleton = () => (
	<div className='flex flex-col'>
		{[1, 2].map((group) => (
			<div key={group} className='flex flex-col'>
				<div className='h-10 border-y border-gray-300 bg-gray-100 flex items-center pl-4'>
					<Skeleton className='h-3 w-32' />
				</div>
				{[1, 2, 3].map((row) => (
					<div
						key={row}
						className='h-11 flex items-center justify-between pl-4 pr-6'
					>
						<Skeleton className='h-3 w-48' />
						<div className='flex items-center gap-2'>
							<Skeleton className='h-6 w-24 rounded-full' />
							<Skeleton className='h-6 w-24 rounded-full' />
							<Skeleton className='h-5 w-9 rounded-full' />
						</div>
					</div>
				))}
			</div>
		))}
	</div>
);

/**
 * One action filter. A real button with tab semantics, not a clickable Badge: a span with an
 * onClick is invisible to the keyboard and to a screen reader, and a row of them is a toolbar
 * nobody can tab into. A native button already fires on Enter and Space, so no key handler is
 * needed. Styled to the design's pill — the active one lifts onto white.
 */
const FilterTab = ({ label, active, onSelect }: { label: string; active: boolean; onSelect: () => void }) => (
	<button
		type='button'
		role='tab'
		aria-selected={active}
		onClick={onSelect}
		className={cn(
			'cursor-pointer rounded-full border border-transparent px-3 py-1.5 text-[13px] font-normal text-gray-800 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
			active && 'border-gray-300 bg-background font-medium text-ink-strong drop-shadow-[0px_2px_4px_rgba(0,0,0,0.04)]'
		)}
	>
		{label}
	</button>
);

export default function SocialMediaAgent() {
	// Must stay in step with the route path: useUrlSearch rewrites the URL to this family.
	const { searchQuery, setSearchQuery } = useUrlSearch('q', '/agents/social-media');
	const [activeFilter, setActiveFilter] = useState<SocialRuleFilter>('all');
	const [channelFilter, setChannelFilter] =
		useState<ChannelFilterValue>(EMPTY_CHANNEL_FILTER);
	const [delayOpen, setDelayOpen] = useState(false);
	const [replyDelay, setReplyDelay] = useState<ReplyDelay | null>(null);
	const [formOpen, setFormOpen] = useState(false);
	const [editingRule, setEditingRule] = useState<SocialRule | null>(null);
	const [ruleToEnable, setRuleToEnable] = useState<SocialRule | null>(null);
	const [ruleToDelete, setRuleToDelete] = useState<SocialRule | null>(null);
	const selectedBusinessUnitId = useAtomValue(selectedBusinessUnitIdAtom);
	const buScope = useMemo(
		() => (selectedBusinessUnitId ? { bu_id: selectedBusinessUnitId } : {}),
		[selectedBusinessUnitId]
	);

	// Read on mount and on every BRAND change, but not alongside the rules: it changes by hand, so
	// refetching it per rule mutation would be a request per toggle for a value nobody touched. The
	// brand is different — a stale delay here is one brand's value shown under another's name.
	useEffect(() => {
		let cancelled = false;
		socialAgentService
			.getSettings(buScope)
			.then((settings) => {
				if (!cancelled) setReplyDelay(settings.replyDelay);
			})
			// A failure must not break the page; the button simply shows no current value.
			.catch((err: unknown) => {
				console.error('[SocialMediaAgent.getSettings] failed to load the reply delay', err);
			});
		return () => {
			cancelled = true;
		};
	}, [buScope]);

	const { data: rules, isLoading, error } = useSocialRules();
	const { data: integrations, isLoading: integrationsLoading } =
		useIntegrations();
	const setStatusMutation = useSetSocialRuleStatus();
	const deleteMutation = useDeleteSocialRule();

	// Rules store channel IDs; the design shows handles. The connected Meta accounts are the only
	// place that mapping exists, and the card already loads them.
	const { handles, allChannels } = useMemo(() => {
		const channels = findMetaChannels(integrations);
		const map: ChannelHandleMap = new Map(
			channels.map((channel) => [
				channel.channelId,
				{ handle: channel.handle, name: channel.name, platform: channel.platform },
			]),
		);
		return {
			handles: map,
			allChannels: channels.map((channel) => ({
				channelId: channel.channelId,
				handle: channel.handle,
				name: channel.name,
				platform: channel.platform,
			})),
		};
	}, [integrations]);

	// Tags are free text with no catalogue behind them, so what the account already tags with is the
	// only list there is to offer — and re-picking one is what stops the helpdesk filling up with
	// near-duplicates of the same tag.
	const tagSuggestions = useMemo(
		() => Array.from(new Set((rules ?? []).flatMap((rule) => rule.actionOptions?.tags ?? []))),
		[rules],
	);

	const filteredRules = useMemo(() => {
		const all = rules ?? [];
		const byAction =
			activeFilter === 'all'
				? all
				: all.filter((rule) => rule.actionType === activeFilter);
		return byAction
			.filter((rule) => matchesSearch(rule, searchQuery))
			.filter((rule) =>
				matchesChannelFilter(rule.channelScope, channelFilter, allChannels),
			);
	}, [rules, activeFilter, searchQuery, channelFilter, allChannels]);

	// A search or a non-default tab can empty the list on an account that has plenty of rules, which
	// is a different thing to say than 'you have no rules yet'.
	/**
	 * The Auto-Reply tab only — not 'All'. A delay applies to replies alone, and offering it from a
	 * view that also lists hides and deletes reads as though it applied to those too.
	 */
	const showsReplies = activeFilter === 'auto_reply';

	const isNarrowed =
		searchQuery.length > 0 ||
		activeFilter !== 'all' ||
		channelFilter.platforms.length > 0 ||
		channelFilter.channelIds.length > 0;
	/**
	 * Hide the filter row while the page is empty. Chips that filter nothing are noise, and the
	 * Figma empty states omit them — this is the one structural difference between the two layouts.
	 */
	const hasAnyRules = (rules ?? []).length > 0;

	const applyStatus = (rule: SocialRule, status: SocialRuleStatus) => {
		setStatusMutation.mutate(
			{ ruleId: rule.id, status },
			{
				onError: (err) =>
					toast.error(
						getApiErrorMessage(
							err,
							`Could not ${status === 'enabled' ? 'enable' : 'disable'} ${rule.name}`,
						),
					),
				// Settled, not success: the confirm dialog holds itself open to show the pending state,
				// so a failed write has to release it too. Closing on the disable path is a no-op.
				onSettled: () => setRuleToEnable(null),
			},
		);
	};

	/**
	 * Turning a rule on asks for confirmation. Turning it off does not, and should not be 'fixed'
	 * to match: disabling only stops the rule acting automatically and is instantly reversible, so
	 * a confirm there is friction with no safety value.
	 */
	const handleToggle = (rule: SocialRule) => {
		if (setStatusMutation.isPending) return;
		if (rule.status === 'enabled') applyStatus(rule, 'disabled');
		else setRuleToEnable(rule);
	};

	const confirmEnable = () => {
		if (!ruleToEnable || setStatusMutation.isPending) return;
		applyStatus(ruleToEnable, 'enabled');
	};

	const handleDelete = (rule: SocialRule) => setRuleToDelete(rule);

	const confirmDelete = () => {
		if (!ruleToDelete || deleteMutation.isPending) return;
		const { id, name } = ruleToDelete;
		deleteMutation.mutate(id, {
			onSuccess: () => toast.success(`Deleted ${name}`),
			onError: (err) =>
				toast.error(getApiErrorMessage(err, `Could not delete ${name}`)),
			onSettled: () => setRuleToDelete(null),
		});
	};

	const handleEdit = (rule: SocialRule) => {
		setEditingRule(rule);
		setFormOpen(true);
	};

	/**
	 * A rule needs somewhere to run. With no Meta channel connected there is no channel to scope it
	 * to and nothing for it to match against, so authoring one would only produce a rule that can
	 * never fire — and the channel picker in the form would be empty.
	 */
	const canCreateRule = allChannels.length > 0;

	/**
	 * Which form a fresh rule opens, now that Create rule asks (design review, 20/08).
	 *
	 * It used to be inferred from the active tab, which meant the same button did different things
	 * depending on where you happened to be — and silently fell back to Auto-Close on the All, Hide
	 * and Delete tabs. Hide and delete stay absent from the menu rather than disabled: they are
	 * Kim-authored, so the server would refuse a brand-written one.
	 */
	const [createActionType, setCreateActionType] = useState<SocialActionType>('auto_close');

	const handleCreate = (actionType: SocialActionType) => {
		setCreateActionType(actionType);
		setEditingRule(null);
		setFormOpen(true);
	};

	const pendingRuleId = setStatusMutation.isPending
		? (setStatusMutation.variables?.ruleId ?? null)
		: null;

	return (
		<div className='flex flex-col h-full'>
			{/* Header */}
			<div className='flex items-center justify-between px-4 h-[60px] shrink-0'>
				<div className='flex items-center gap-1'>
					<h1 className='text-base font-semibold text-ink'>
						Social media agent
					</h1>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant='ghost'
								size='icon-sm'
								aria-label='About the social media agent'
							>
								<Info className='size-3.5 text-gray-800' />
							</Button>
						</TooltipTrigger>
						<TooltipContent variant='dark' className='max-w-xs'>
							Rules decide what Kim does with comments on your connected
							Facebook and Instagram accounts. Exactly one rule runs per
							comment.
						</TooltipContent>
					</Tooltip>
				</div>

				<div className='flex items-center gap-3'>
					{/* Search and the secondary button are ONE group at 6px, set 12px away from the divider
							and the primary action. The design pairs them deliberately — both are ways of getting
							at the rules already on the page, where Create rule adds one. Spacing everything
							evenly at 12px lost that grouping. */}
					<div className='flex items-center gap-1.5'>
						<SearchInput
							className='h-8 w-[280px]'
							placeholder='Search'
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
						{/* The design puts Playground in this slot. Deferred, and left out rather than
								rendered inert — a button that does nothing reads as broken. The action log takes
								the slot instead: it reads the rules on this page, so it belongs beside them
								rather than as a sibling entry in the sidebar. */}
						<Button
							asChild
							variant='outline'
							className={cn(PILL_BUTTON, 'gap-2')}
						>
							<Link to='/agents/action-log'>
								<ScrollText className='size-3.5' /> View action logs
							</Link>
						</Button>
					</div>
					<Separator orientation='vertical' className='h-[19px]' />
					<Tooltip>
						<TooltipTrigger asChild>
							{/* A disabled button swallows pointer events, so the trigger wraps it — otherwise
									the tooltip explaining WHY it is disabled would never appear. */}
							<span tabIndex={canCreateRule ? -1 : 0}>
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											className={cn(PILL_BUTTON, 'gap-2')}
											disabled={!canCreateRule}
										>
											<Plus className='size-3.5' /> Create rule
										</Button>
									</DropdownMenuTrigger>
									{/* No width override: the longest label is 'Auto-Reply', and 180px left a band of
											empty space beside all three. The shared menu's own 8rem floor is plenty. */}
									<DropdownMenuContent align='end'>
										{CREATABLE_ACTION_TYPES.map((actionType) => (
											<DropdownMenuItem
												key={actionType}
												className='text-[13px]'
												onSelect={() => handleCreate(actionType)}
											>
												{SOCIAL_ACTION_LABELS[actionType]}
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							</span>
						</TooltipTrigger>
						{!canCreateRule && (
							<TooltipContent variant='dark'>Connect a Meta account first — a rule needs a channel to run on.</TooltipContent>
						)}
					</Tooltip>
				</div>
			</div>

			{/* Filter tabs */}
			{hasAnyRules && (
				<div className='flex items-center justify-between px-4 pt-3 pb-4 shrink-0'>
					<div role='tablist' aria-label='Filter rules by action' className='flex items-center gap-2'>
						{SOCIAL_RULE_FILTERS.map((filter) => (
							<FilterTab
								key={filter.value}
								label={filter.label}
								active={activeFilter === filter.value}
								onSelect={() => setActiveFilter(filter.value)}
							/>
						))}
					</div>

					<div className='flex items-center gap-2'>
						{/*
						 * Delay reply sits beside Filter per Figma 4241:78825, and only on the Auto-Reply
						 * views — the setting applies to replies alone, so offering it while looking at
						 * Auto-Hide would imply it did something there.
						 */}
						{/* A plain secondary button, no chevron (design review, 20/08). The chevron implied a
								dropdown; this opens a modal, so it promised the wrong interaction. */}
						{showsReplies && (
							<Button
								variant='outline'
								className={cn(PILL_BUTTON, 'px-4')}
								onClick={() => setDelayOpen(true)}
							>
								{delayButtonLabel(replyDelay)}
							</Button>
						)}

						<ChannelFilter
							channels={allChannels}
							value={channelFilter}
							onChange={setChannelFilter}
						/>
					</div>
				</div>
			)}

			{/* Rules */}
			<div className='flex-1 overflow-auto'>
				{isLoading && <RulesTableSkeleton />}

				{error && (
					<p className='px-4 py-8 text-center text-sm text-destructive'>
						Could not load rules. Please try again.
					</p>
				)}

				{!isLoading && !error && filteredRules.length === 0 && isNarrowed && (
					<p className='px-4 py-8 text-center text-sm text-muted-foreground'>
						No rules match this view.
					</p>
				)}

				{/* The full empty state belongs to a page with nothing on it, not to a search that
				    found nothing — narrowing to zero keeps the one-line message above. It also waits
				    on the integrations query, so the call to action does not read 'Connect meta' for
				    a frame and then change once the already-connected channels arrive. */}
				{!isLoading &&
					!error &&
					!integrationsLoading &&
					filteredRules.length === 0 &&
					!isNarrowed && (
						/* The empty state has no rules to infer a type from, and its own copy says
							 'Create rules' — Auto-Close is the least consequential thing to open on. */
						<SocialAgentEmptyState
							hasChannels={allChannels.length > 0}
							onCreateRule={() => handleCreate('auto_close')}
						/>
					)}

				{!isLoading && !error && filteredRules.length > 0 && (
					<RulesTable
						rules={filteredRules}
						handles={handles}
						allChannels={allChannels}
						pendingRuleId={pendingRuleId}
						onToggle={handleToggle}
						onEdit={handleEdit}
						onDelete={handleDelete}
					/>
				)}
			</div>

			<DelayReplyDialog
				open={delayOpen}
				onOpenChange={setDelayOpen}
				replyDelay={replyDelay}
				onSaved={setReplyDelay}
			/>

			<RuleFormDialog
				open={formOpen}
				onOpenChange={setFormOpen}
				rule={editingRule}
				actionType={createActionType}
				channels={allChannels}
				tagSuggestions={tagSuggestions}
				onSaved={() => invalidateQueries(['social-agent'])}
			/>

			<ConfirmDialog
				open={ruleToEnable !== null}
				title='Enable rule?'
				description='Once enabled, this rule will run automatically on matching comments and take the defined action'
				confirmLabel='Enable'
				isPending={setStatusMutation.isPending}
				onConfirm={confirmEnable}
				onCancel={() => setRuleToEnable(null)}
			/>

			<ConfirmDialog
				open={ruleToDelete !== null}
				title='Delete rule ?'
				description='Deleting this rule is permanent. Do you want to continue?'
				confirmLabel='Delete'
				destructive
				isPending={deleteMutation.isPending}
				onConfirm={confirmDelete}
				onCancel={() => setRuleToDelete(null)}
			/>
		</div>
	);
}
